<template>
  <div
    class="mohg-slider"
    tabindex="0"
    role="slider"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :aria-valuenow="ariaValueNow"
    :aria-valuetext="ariaValueText"
    :aria-label="ariaLabel"
    @touchstart.stop="updateStart"
    @mousedown.stop="updateStart"
    @focus="handleFocus"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @keyup="handleKeyup"
  >
    <div
      ref="background"
      class="background"
    />
    <div
      class="fill"
      :style="[{ width: progress + '%' }]"
    />
    <div
      class="handle"
      :style="[{ 'margin-left': progress + '%' }]"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  export default {
    name: 'Slider',
    props: {
      value: {
        type: Number,
        default: 0
      },
      max: {
        type: Number,
        default: 1
      },
      min: {
        type: Number,
        default: 0
      },
      ariaLabel: {
        type: String,
        default: ''
      },
      sliderType: {
        type: String,
        default: 'default'
      }
    },
    data: () => ({
      internalValue: 0,
      startPointRelative: null,
      startPoint: null,
      offset: null,
      isInteracting: false
    }),
    computed: {
      ...mapGetters('i18n', ['tc']),
      progress() {
        return (this.value - this.min) / (this.max - this.min) * 100
      },
      ariaValueNow() {
        if (this.sliderType === 'volume') {
          return `${Math.round(this.progress)}`
        }
        return Math.round(this.internalValue)
      },
      ariaValueText() {
        if (this.sliderType === 'volume') {
          return this.tc('videoPlayer.volumeLevel', { percentage: Math.round(this.progress) })
        }
        const currentTime = this.formatTime(Math.round(this.internalValue))
        const totalTime = this.formatTime(Math.round(this.max))
        return this.tc('videoPlayer.currentPlaybackTime', { currentTime, totalTime })
      }
    },
    watch: {
      internalValue(val) {
        this.$emit('input', val)
      },
      value(val) {
        this.internalValue = val
      }
    },
    created() {
      this.internalValue = this.value
    },
    methods: {
      updateStart(e) {
        e.preventDefault()
        this.$emit('interact', true)
        this.updateValue(e)

        const isTouchEvent = 'touches' in e
        window.addEventListener(isTouchEvent ? 'touchmove' : 'mousemove', this.updateValue)
        window.addEventListener(isTouchEvent ? 'touchend' : 'mouseup', this.updateEnd)
      },
      updateValue(e) {
        e.preventDefault()
        const { width, left } = this.$refs.background.getBoundingClientRect()
        const { clientX } = 'touches' in e ? e.touches[0] : e
        const percent = (clientX - left) / width
        const newValue = this.min + percent * (this.max - this.min)
        if (newValue >= this.min && newValue <= this.max) {
          this.internalValue = newValue
        }
      },
      updateEnd(e) {
        e.preventDefault()
        this.$emit('interact', false)
        const isTouchEvent = 'touches' in e
        window.removeEventListener(isTouchEvent ? 'touchmove' : 'mousemove', this.updateValue)
        window.removeEventListener(isTouchEvent ? 'touchend' : 'mouseup', this.updateEnd)
      },
      handleKeydown(e) {
        const interval = Math.min(this.max / 10, 10)
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault()
          if (this.internalValue < this.max) {
            this.internalValue += interval
            if (this.internalValue > this.max) this.internalValue = this.max
            this.$emit('interact', true)
            this.isInteracting = true
          }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault()
          if (this.internalValue > this.min) {
            this.internalValue -= interval
            if (this.internalValue < this.min) this.internalValue = this.min
            this.$emit('interact', true)
            this.isInteracting = true
          }
        }
      },
      handleKeyup(e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          this.$emit('interact', false)
          this.isInteracting = false
        }
      },
      handleFocus() {
        this.$emit('focus')
      },
      handleBlur() {
        this.$emit('blur')
      },
      formatTime(seconds) {
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60
        return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
      }
    }
  }
</script>
